import {
  FC,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  IAutoSelectOption,
  Select,
  SelectAutocomplete,
  SelectGroupDivider,
  SelectItem,
} from 'team-hero-ui';
import { useTranslation } from 'react-i18next';

import type { IOption } from 'components/Modals/AddEditModal/modalConfig/modalConfig.interface';
import type {
  IOptionGroupWithItems,
  ISelectFieldProps,
} from './SelectField.types';
import ValidationWrapper from 'components/Modals/AddEditModal/ModalValidationWrapper';
import { useGetSelectExternalOptionValues } from 'hooks/getSelectExternalOptionValues/getSelectExternalOptionValues.hook';
import { createOptionSelectGroups } from './helper/selectOptions.helper';

const defaultArray: [] = [];

const SelectField: FC<ISelectFieldProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  validationText,
  options,
  externalDataSource,
  hint,
  styleType = 'modal',
  enableEmptySelect = true,
  disableValidation = false,
  inputRef,
  optionGroups = defaultArray,
  hideValidationBorder = false,
  withAutocomplete = false,
}) => {
  const [items, setItems] = useState<IOption[]>(options || []);
  const { t, i18n } = useTranslation('general');

  const { data: externalDataValues, isLoading } =
    useGetSelectExternalOptionValues(externalDataSource);

  useEffect(() => {
    if (options?.length) {
      const selectOptions = options?.map((option) => ({
        label: i18n.exists(option.label) ? t(option.label) : option.label,
        value: option.value,
      }));

      setItems(selectOptions);
    }
  }, [options, t, i18n]);

  const selectItems = externalDataSource ? externalDataValues : items;

  const selectItemList = (): ReactElement[] => {
    // if optionGroups are defined, return the grouped selectItems
    // not grouped items, will be put to others group
    if (optionGroups.length > 0) {
      const optionSelectGroups = createOptionSelectGroups(
        optionGroups,
        selectItems,
        t
      );
      const optionsReturn: ReactElement[] = [];
      optionSelectGroups.forEach((optionGroup: IOptionGroupWithItems) => {
        if (optionGroup.values.length === 0) return;
        optionsReturn.push(
          <SelectGroupDivider
            key={`group-divider_${optionGroup.label}`}
            title={optionGroup.label}
          />
        );
        optionGroup.values.forEach((item) => {
          optionsReturn.push(
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          );
        });
      });
      return optionsReturn;
    }
    // if no optionGroups are defined, return the default selectItems
    return selectItems.map((item) => {
      return (
        <SelectItem key={item?.value?.toString()} value={item.value}>
          {item.label}
        </SelectItem>
      );
    });
  };

  const selectRenderer = () => (
    <Select
      label={`${label}${required ? '*' : ''}`}
      value={value as string}
      onChange={(selectedValue) => onChange(selectedValue as string)}
      styleType={styleType}
      helperText={hint}
      disabled={disabled}
      inputRef={
        inputRef as (
          ref:
            | HTMLSelectElement
            | { node: HTMLInputElement; value: string | number | undefined }
        ) => void
      }
    >
      {enableEmptySelect && (
        <SelectItem key='empty' value=''>
          {t('select.noValue')}
        </SelectItem>
      )}
      {selectItemList()}
    </Select>
  );

  const styleTypeAutoComplete = styleType as
    | 'default'
    | 'modal'
    | 'formUniversal'
    | 'filter';

  const onChangeCallback = useCallback(
    (
      _event: SyntheticEvent<Element, Event>,
      changeValue: IAutoSelectOption
    ) => {
      if (changeValue) {
        onChange(changeValue.value.toString());
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  if (disableValidation) {
    return selectRenderer();
  }

  if (withAutocomplete) {
    return (
      <ValidationWrapper
        hideBorder={hideValidationBorder}
        text={validationText}
      >
        <SelectAutocomplete
          disabled={isLoading || disabled}
          label={`${label}${required ? '*' : ''}`}
          value={value || null}
          onChange={onChangeCallback}
          styleType={styleTypeAutoComplete}
          options={options || []}
          onInputChange={() => {}}
          freeSolo={false}
          helperText={hint}
          inputRef={inputRef}
        />
      </ValidationWrapper>
    );
  }

  return (
    <ValidationWrapper hideBorder={hideValidationBorder} text={validationText}>
      {selectRenderer()}
    </ValidationWrapper>
  );
};

export default SelectField;
